import React, { useState } from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { Button, Input, Label, TextArea } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Row } from 'reactstrap';
import { CaseCaseRegistryModel } from 'app/edozvola/cases/cases.model';
import { useNavigate, useParams } from 'react-router-dom';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { useSelector } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { IRootState, useAppDispatch } from 'app/config/store';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { fetchCaseCategoryForCaseQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { Loader } from 'app/shared/loader/loader';
import { caseRegistryFormValidation } from './model/case-case-registry.schema';
import { fetchCaseCaseRegistryQuery, saveCaseCaseRegistryMutation } from './service/case-registry.queries';
import { getCaseContext } from 'app/shared/util/context-util';
import { ConfirmationModal } from 'app/shared/modal/confirmation-modal';

const CaseCaseRegistryForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);

  const appDispatch = useAppDispatch();
  const selectedCase = useSelector((state: IRootState) => state.caseEntity);

  const { data: caseCategoryOptions, isLoading: caseCategoryOptionsLoading } = fetchCaseCategoryForCaseQuery(id);
  const { data: caseRegistryData, isLoading: caseRegistryDataLoading } = fetchCaseCaseRegistryQuery(id);

  const { sidebarItemPair } = getCaseContext();

  const navigateToCases = (data: any) => {
    if (selectedCase.caseCategory !== data.caseCategory[0].value) {
      navigate('/cases');
    } else {
      formik.resetForm({values: data})
    }
  };
  const { mutate: saveCaseCaseRegistry, isPending: savingInProgress } = saveCaseCaseRegistryMutation(id, data => {
    if (selectedCase.caseCategory === data.caseCategory[0].value) {
      formik.resetForm({values: data});
    }
    setConfirmationModalOpened(false);
  });

  const checkIfCaseCategoryIsChanged = values => {
    if (selectedCase.caseCategory !== values.caseCategory[0].value) {
      setConfirmationModalOpened(true);
    } else {
      saveCaseCaseRegistry(values);
    }
  };

  const formik = useFormik({
    initialValues: caseRegistryData || ({} as CaseCaseRegistryModel),
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: caseRegistryFormValidation,
    onSubmit: values => checkIfCaseCategoryIsChanged(values),
  });

  return (
    <>
      {caseRegistryDataLoading && <Loader />}
      {!caseRegistryDataLoading && (
        <FormikProvider value={formik}>
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  appDispatch(setFormChanged(true));
                } else {
                  appDispatch(initCaseFormChange());
                }
              }}
            />
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Vrsta predmeta'} />
                  <Field
                    id="caseCategory"
                    name="caseCategory"
                    render={({ field }) => (
                      <PaginatedDropdown
                        onItemSelect={selectedOption => formik.setFieldValue('caseCategory', selectedOption)}
                        selected={formik.values.caseCategory ?? []}
                        labelKey={'value'}
                        options={caseCategoryOptionsLoading ? [] : caseCategoryOptions}
                        placeholder={!sidebarItemPair?.readOnly && 'Odaberi vrstu predmeta'}
                        disabled={sidebarItemPair?.readOnly}
                        error={!sidebarItemPair?.readOnly && formik.errors.caseCategory as string}
                        showErrorMessage
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Grupa predmeta'} />
                  <Field
                    id="caseGroup"
                    name="caseGroup"
                    render={({ field }) => (
                      <PaginatedDropdown
                        placeholder={'Odaberi grupu predmeta'}
                        onItemSelect={() => {}}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={[]}
                        disabled={true}
                        error={formik.errors.caseGroup as string}
                        showErrorMessage
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Skupina predmeta'} />
                  <Field id="caseType" name="caseCollection" render={({ field }) => <Input {...field} disabled={true} type="input" />} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Šifra predmeta'} />
                  <Field id="webId" name="webId" render={({ field }) => <Input {...field} disabled={true} type="input" />} />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Klasifikacijska oznaka'} />
                  <Field
                    id="classification"
                    name="classification"
                    render={({ field }) => <Input {...field} disabled={true} type="input" />}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Datum klasificiranja'} />
                  <Field
                    id="classificationDate"
                    name="classificationDate"
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} dateFormat={APP_DATE_FORMAT} disabled={true} />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Vrsta postupka'} />
                  <Field
                    id="caseTypeClassification"
                    name="caseTypeClassification"
                    render={({ field }) => <Input {...field} disabled={true} type="input" />}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Klasifikacijska grupa'} />
                  <Field
                    id="classificationGroup"
                    name="classificationGroup"
                    render={({ field }) => <Input {...field} disabled={true} type="input" />}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Dosje'} />
                  <Field id="file" name="file" render={({ field }) => <Input {...field} disabled={true} type="input" />} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Veza'} />
                  <Field
                    id="relation"
                    name="relation"
                    render={({ field }) => <TextArea {...field} type="input"
                                                     error={!sidebarItemPair?.readOnly && formik.errors.relation}
                                                     disabled={sidebarItemPair?.readOnly} />}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Šifra povezanog predmeta'} />
                  <Field
                    id="copiedCaseWebId"
                    name="copiedCaseWebId"
                    render={({ field }) => <Input {...field} type="input" disabled={true} />}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Kratki opis'} />
                  <Field
                    id="description"
                    name="description"
                    render={({ field }) => <TextArea {...field} type="input"
                                                     error={!sidebarItemPair?.readOnly && formik.errors.description}
                                                     disabled={sidebarItemPair?.readOnly} />}
                  />
                </FormGroup>
              </Col>
            </Row>

            {!sidebarItemPair?.readOnly && (
              <div className="case-save-button">
                <Button color={'info'} isLoading={savingInProgress} label={'Spremi'} size={'lg'} onClick={formik.submitForm} disabled={!formik.dirty || !formik.isValid} />
              </div>
            )}
          </Form>
        </FormikProvider>
      )}

      <ConfirmationModal
        modalOpened={confirmationModalOpened}
        header={'Promjena vrste predmeta'}
        bodyMessage={
          'Promjenom vrste predmeta izmijenit će se prikaz kartica na predmetu te ćete biti preusmjereni na početni ekran s popisom predmeta! Želite li potvrditi promjenu vrste predmeta?'
        }
        onConfirm={() => saveCaseCaseRegistry(formik.values)}
        onCancel={() => setConfirmationModalOpened(false)}
      />
    </>
  );
};

export default CaseCaseRegistryForm;
